import { useCallback, useEffect, useRef, useState } from 'react';

const METERS_PER_MILE = 1609.344;

// Browser geolocation error codes (GeolocationPositionError).
const PERMISSION_DENIED = 1;
const POSITION_UNAVAILABLE = 2;
const TIMEOUT = 3;

export const LOCATION_MESSAGES = {
    unsupported: "This browser can't share your location. Search by ZIP code instead.",
    denied: 'Location permission was denied. Allow location for this site in your browser settings, or search by ZIP code.',
    unavailable: "Your location isn't available right now. Check that location services are on, or search by ZIP code.",
    timeout: 'Finding your location took too long. Try again, or search by ZIP code.',
};

// Matches the browser test that works: network (Wi-Fi/IP) positioning is enough for a
// shop search and answers quickly on desktops, and maximumAge 0 always asks for a fresh
// position instead of reusing a cached one.
const GEOLOCATION_OPTIONS = { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 };
// The browser's own timeout only starts once permission is granted, so a permission
// prompt the driver never answers would leave "Finding your location..." on screen
// forever. After this long without any answer, show the timeout message instead.
export const LOCATION_WATCHDOG_MS = 30000;

// Browser accuracy (meters) shown in miles: "less than 0.1 mi", "0.4 mi", "12 mi".
export function formatAccuracyMiles(meters) {
    const miles = Number(meters) / METERS_PER_MILE;
    if (!Number.isFinite(miles) || miles < 0) return '';
    if (miles < 0.1) return 'less than 0.1 mi';
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
}

function errorKey(error) {
    if (error?.code === PERMISSION_DENIED) return 'denied';
    if (error?.code === TIMEOUT) return 'timeout';
    if (error?.code === POSITION_UNAVAILABLE) return 'unavailable';
    return 'unavailable';
}

/**
 * The driver's current location, asked for only when `request()` is called (a click), so
 * the browser's permission prompt never appears on its own. The position lives in this
 * component's state only: nothing is written to storage or sent anywhere, and `clear()`
 * forgets it.
 *
 * state: { status: 'idle' | 'locating' | 'found' | 'error', position, errorKey }
 * position: { latitude, longitude, accuracyMeters, accuracyMiles }
 */
export default function useCurrentLocation() {
    const [state, setState] = useState({ status: 'idle', position: null, errorKey: '' });
    const requestIdRef = useRef(0);
    const mountedRef = useRef(false);
    const watchdogRef = useRef(null);

    // Set on every mount, not just at creation: React StrictMode (development) mounts,
    // unmounts and mounts again, and a ref left false by that first cleanup made every
    // geolocation answer look like it arrived after unmount, so it was dropped and the
    // drawer stayed on "Finding your location..." (#697).
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            window.clearTimeout(watchdogRef.current);
        };
    }, []);

    const request = useCallback(() => {
        const geolocation = typeof navigator !== 'undefined' ? navigator.geolocation : null;
        if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
            setState({ status: 'error', position: null, errorKey: 'unsupported' });
            return;
        }
        const requestId = ++requestIdRef.current;
        const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;
        setState((prev) => ({ ...prev, status: 'locating', errorKey: '' }));
        window.clearTimeout(watchdogRef.current);
        watchdogRef.current = window.setTimeout(() => {
            if (!isCurrent()) return;
            // Still waiting (e.g. the permission prompt was ignored): stop the spinner. A
            // position that arrives later is still accepted.
            setState((prev) => (prev.status === 'locating' ? { status: 'error', position: null, errorKey: 'timeout' } : prev));
        }, LOCATION_WATCHDOG_MS);
        geolocation.getCurrentPosition(
            ({ coords }) => {
                window.clearTimeout(watchdogRef.current);
                if (!isCurrent()) return;
                setState({
                    status: 'found',
                    errorKey: '',
                    position: {
                        latitude: coords.latitude,
                        longitude: coords.longitude,
                        accuracyMeters: coords.accuracy,
                        accuracyMiles: formatAccuracyMiles(coords.accuracy),
                    },
                });
            },
            (error) => {
                window.clearTimeout(watchdogRef.current);
                if (!isCurrent()) return;
                setState({ status: 'error', position: null, errorKey: errorKey(error) });
            },
            GEOLOCATION_OPTIONS,
        );
    }, []);

    const clear = useCallback(() => {
        requestIdRef.current += 1; // ignore an answer still on its way
        window.clearTimeout(watchdogRef.current);
        setState({ status: 'idle', position: null, errorKey: '' });
    }, []);

    return { ...state, message: state.errorKey ? LOCATION_MESSAGES[state.errorKey] : '', request, clear };
}
